import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash';
import Scrollbars from 'react-custom-scrollbars-2';
import { Checkbox, DefaultButton, FontIcon, Label, Spinner, SpinnerSize } from '@fluentui/react';
import { Pbs } from '../../../models/pbs';
import { getPbs } from '../../../redux/reducers/sharedData';
import { getFilterPbs } from '../../../redux/reducers/filters';
import { CommandActions as FiltersCommandActions } from '../../../redux/actions/filters';
import { CommandActions as DocumentsCommandActions } from '../../../redux/actions/documents';
import { getLoader } from '../../../redux/selectors/loader';
import { getClassNames } from './index.classNames';

interface PbsTreeNode {
    item: Pbs,
    children: PbsTreeNode[]
}

interface PbsTreeViewProps {
    pbs: Pbs[],
    selectedPbs?: Pbs[],
    loadingPbs: boolean,
    setPbs: typeof FiltersCommandActions.setPbs,
    fetchDocuments: typeof DocumentsCommandActions.fetchDocuments,
}

interface PbsTreeViewState {
    expandedNodes: {[key: string]: boolean}
    expansionMode: 'expanded' | 'collapsed' | 'custom'
}

const pbsTypeOrder: {[key: string]: number} = {
    'Plant': 0,
    'Plant Unit': 1,
    'Plant Section': 2,
    'Equipment': 3,
};

class PbsTreeView extends React.Component<PbsTreeViewProps, PbsTreeViewState> {
    debouncedFetchDocuments: Function

    constructor(props: PbsTreeViewProps) {
        super(props);

        this.state = {
            expandedNodes: this.getExpandedNodes(props.pbs, 'expanded'),
            expansionMode: 'expanded'
        };

        this.debouncedFetchDocuments = _.debounce(() => {
            const { fetchDocuments } = this.props;
            fetchDocuments({});
        }, 500);
    }

    componentDidUpdate(prevProps: PbsTreeViewProps) {
        if (prevProps.pbs !== this.props.pbs) {
            this.setState((prevState: PbsTreeViewState) => ({
                expandedNodes: this.getExpandedNodes(this.props.pbs, prevState.expansionMode, prevState.expandedNodes)
            }));
        }
    }

    getExpandedNodes = (pbs: Pbs[], expansionMode: 'expanded' | 'collapsed' | 'custom', previousExpandedNodes?: {[key: string]: boolean}): {[key: string]: boolean} => {
        return _.reduce(pbs, (result: {[key: string]: boolean}, pbsItem: Pbs) => {
            if(expansionMode === 'expanded')
                result[pbsItem.id] = true;
            else if(expansionMode === 'collapsed')
                result[pbsItem.id] = false;
            else
                result[pbsItem.id] = previousExpandedNodes && previousExpandedNodes[pbsItem.id] !== undefined ? previousExpandedNodes[pbsItem.id] : true;
            return result;
        }, {});
    }

    getTreeNodes = (): PbsTreeNode[] => {
        const { pbs } = this.props;
        const itemsByParent = _.groupBy(pbs, (pbsItem: Pbs) => pbsItem.parentId || '__root__');
        const sortItems = (items: Pbs[]) => _.sortBy(items, [
            (pbsItem: Pbs) => pbsTypeOrder[pbsItem.type] || 99,
            (pbsItem: Pbs) => pbsItem.name
        ]);

        const buildChildren = (parentId?: string): PbsTreeNode[] => {
            const key = parentId || '__root__';
            return _.map(sortItems(itemsByParent[key] || []), (pbsItem: Pbs) => ({
                item: pbsItem,
                children: buildChildren(pbsItem.id)
            }));
        };

        return buildChildren();
    }

    onToggleExpand = (nodeId: string) => {
        this.setState((prevState: PbsTreeViewState) => ({
            expandedNodes: {
                ...prevState.expandedNodes,
                [nodeId]: !prevState.expandedNodes[nodeId]
            },
            expansionMode: 'custom'
        }));
    }

    expandAll = () => {
        this.setState({expandedNodes: this.getExpandedNodes(this.props.pbs, 'expanded'), expansionMode: 'expanded'});
    }

    collapseAll = () => {
        this.setState({
            expandedNodes: this.getExpandedNodes(this.props.pbs, 'collapsed'),
            expansionMode: 'collapsed'
        });
    }

    onToggleSelection = (pbsItem: Pbs, checked?: boolean) => {
        const { selectedPbs, setPbs } = this.props;
        const currentSelected = selectedPbs || [];
        const updatedPbs = checked
            ? (_.some(currentSelected, selectedItem => selectedItem.id === pbsItem.id)
                ? currentSelected
                : [...currentSelected, pbsItem])
            : _.filter(currentSelected, selectedItem => selectedItem.id !== pbsItem.id);

        setPbs({value: updatedPbs.length > 0 ? updatedPbs : undefined});
        this.debouncedFetchDocuments();
    }

    renderNode = (node: PbsTreeNode, level: number): JSX.Element => {
        const { selectedPbs } = this.props;
        const { expandedNodes } = this.state;
        const styles = getClassNames();
        const isExpanded = expandedNodes[node.item.id];
        const hasChildren = node.children.length > 0;
        const isChecked = _.some(selectedPbs, selectedItem => selectedItem.id === node.item.id);

        return (
            <div key={node.item.id}>
                <div className={isChecked ? styles.nodeRowSelected + ' ' + styles.nodeRow : styles.nodeRow} style={{paddingLeft: 12 + (level * 20)}}>
                    <div className={styles.nodeInfo}>
                        {hasChildren ? (
                            <FontIcon
                                className={styles.chevron}
                                iconName={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                                onClick={() => this.onToggleExpand(node.item.id)}
                            />
                        ) : (
                            <div className={styles.chevronPlaceholder} />
                        )}
                        <div className={styles.nodeLabelBlock}>
                            <span className={styles.nodeText} title={node.item.name}>{node.item.name}</span>
                            <span className={styles.nodeDescription} title={node.item.description}>{node.item.description}</span>
                        </div>
                    </div>
                    <Checkbox
                        checked={isChecked}
                        onChange={(event, checked) => this.onToggleSelection(node.item, checked)}
                    />
                </div>
                {hasChildren && isExpanded && _.map(node.children, (childNode: PbsTreeNode) => this.renderNode(childNode, level + 1))}
            </div>
        );
    }

    render() {
        const { loadingPbs, pbs } = this.props;
        const styles = getClassNames();
        const treeNodes = this.getTreeNodes();

        return (
            <div className={styles.panel}>
                <div className={styles.panelHeader}>
                    <div className={styles.panelHeaderTitle}>
                        <FontIcon iconName="OpenSource" style={{ fontSize: 16, color: 'green' }} />
                        <div>PBS Tree view</div>
                    </div>
                    <div className={styles.panelHeaderActions}>
                        <DefaultButton
                            text="Expand all"
                            onClick={this.expandAll}
                            styles={{root: {minWidth: 0, padding: '0 8px', height: 28}}}
                        />
                        <DefaultButton
                            text="Collapse all"
                            onClick={this.collapseAll}
                            styles={{root: {minWidth: 0, padding: '0 8px', height: 28}}}
                        />
                    </div>
                </div>
                <div className={styles.panelBody}>
                    {loadingPbs &&
                        <div style={{display: 'flex', alignItems: 'center', gap: 10, padding: 16}}>
                            <Label styles={{root: {marginBottom: 0}}}>Loading PBS</Label>
                            <Spinner size={SpinnerSize.small} />
                        </div>
                    }
                    {!loadingPbs && pbs.length === 0 &&
                        <div className={styles.emptyState}>No PBS available</div>
                    }
                    {!loadingPbs && pbs.length > 0 &&
                        <Scrollbars style={{height: '97%'}}>
                            <div style={{padding: '8px 0 12px 0'}}>
                                {_.map(treeNodes, (node: PbsTreeNode) => this.renderNode(node, 0))}
                            </div>
                        </Scrollbars>
                    }
                </div>
            </div>
        );
    }
}

const mapStateToProps = createStructuredSelector({
    pbs: getPbs,
    selectedPbs: getFilterPbs,
    loadingPbs: (state) => getLoader('projectShared')(state).loadingPbs,
});

function matchDispatchToProps(dispatch: any) {
    return bindActionCreators({
        setPbs: FiltersCommandActions.setPbs,
        fetchDocuments: DocumentsCommandActions.fetchDocuments,
    }, dispatch);
}

export default connect(mapStateToProps, matchDispatchToProps)(PbsTreeView);